/**
 * Atribución de leads a Google Ads.
 *
 * Al entrar en la web guardamos el identificador de click de anuncio que añade
 * Google (`gclid`, `wbraid` o `gbraid`) en una cookie de 90 días, y luego lo
 * arrastramos hasta el mensaje de WhatsApp y el formulario de contacto. Así
 * cada lead llega con su referencia y podemos cruzarlo para subir las
 * conversiones/ventas a Google Ads. No depende de Tag Manager.
 */

/** Tipos de id que usa Google, en orden de prioridad. */
const CLICK_ID_KEYS = ['gclid', 'wbraid', 'gbraid'] as const;

/** Vida de la cookie: 90 días en segundos (mismo margen que la ventana de conversión). */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

/**
 * Lee el id de click de la URL actual y lo guarda en cookie.
 * Llamar una sola vez al cargar la app (useEffect del componente raíz).
 */
export function captureClickId(): void {
  const params = new URLSearchParams(window.location.search);
  for (const key of CLICK_ID_KEYS) {
    const value = params.get(key);
    if (value) {
      document.cookie = `${key}=${encodeURIComponent(value)}; max-age=${MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
    }
  }
}

/**
 * Devuelve el id guardado con prioridad `gclid` > `wbraid` > `gbraid`,
 * en formato `tipo:valor` (p. ej. `gclid:test123`). Si no hay ninguno,
 * devuelve `'directo'`.
 */
export function getClickId(): string {
  for (const key of CLICK_ID_KEYS) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + key + '=([^;]+)'));
    if (match) return `${key}:${decodeURIComponent(match[1])}`;
  }
  return 'directo';
}

/**
 * Construye la URL de wa.me con el mensaje prerrellenado y la referencia del
 * lead. Se calcula en el momento del click, así la cookie ya está guardada.
 */
export function buildWhatsAppUrl(baseUrl: string): string {
  const text = encodeURIComponent(`Hola, quiero información (ref: ${getClickId()})`);
  return `${baseUrl}?text=${text}`;
}
